import { Link } from "react-router-dom";
import { FadeUp, SlideInLeft, SlideInRight, ScaleIn, StaggerContainer } from "@/components/base/ScrollAnimations";
import { companyInfo, whyChooseUs, serviceAreas } from "@/mocks/websiteData";

export default function About() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20solar%20energy%20company%20team%20meeting%20in%20bright%20office%20with%20solar%20panel%20samples%20and%20project%20plans%20on%20table%2C%20diverse%20Indian%20professionals%20in%20casual%20attire%2C%20warm%20natural%20lighting%2C%20professional%20corporate%20photography&width=1600&height=700&seq=about-team-01&orientation=landscape"
          alt="NexSunNexova team"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/85"></div>
        <div className="relative z-10 section-padding text-center w-full">
          <FadeUp>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/25 backdrop-blur-sm border border-accent-400/30 mb-4">
              <span className="text-sm font-medium text-accent-300">Since 2020 · Telangana</span>
            </div>
          </FadeUp>
          <FadeUp>
            <p className="text-sm font-medium text-background-100/80 mb-2 tracking-wide">NexSunNexova Solar Energy Pvt. Ltd.</p>
          </FadeUp>
          <FadeUp delay={100}>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mb-3">About Us</h1>
          </FadeUp>
          <FadeUp delay={200}>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto">Building a sustainable future, one solar panel at a time.</p>
          </FadeUp>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-6xl mx-auto">
            <SlideInLeft>
              <div>
                <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Our Story</span>
                <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2 mb-4">Leading Solar Innovation in Telangana</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {companyInfo.name} was founded with a mission to make clean, affordable solar energy accessible to every home, farm, and business in Telangana and beyond. Under the leadership of our Founder & Managing Director <strong>Shaik Hyder Pasha</strong>, we have grown into a trusted name in solar EPC (Engineering, Procurement, Construction) services.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  We are a government-registered private limited company (Reg. No. {companyInfo.regNo}) with a valid GST registration ({companyInfo.gstNo}). Our end-to-end solutions cover everything from residential rooftop installations to large-scale commercial solar power plants, solar water pumps for agriculture, and innovative products like EV charging stations and agricultural spray drones.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  What sets us apart is our commitment to transparency — we publish our pricing upfront, help customers claim government subsidies of up to {companyInfo.subsidyAmount}, and provide comprehensive after-sales AMC support to ensure your solar investment delivers returns for decades.
                </p>
              </div>
            </SlideInLeft>

            <SlideInRight delay={150}>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="relative rounded-xl overflow-hidden group">
                  <img
                    src="https://readdy.ai/api/search-image?query=Close%20up%20of%20solar%20panel%20installation%20on%20Indian%20residential%20roof%20during%20daytime%2C%20blue%20panels%20with%20metal%20mounting%2C%20clear%20sky%2C%20professional%20photography%2C%20warm%20natural%20light&width=500&height=400&seq=about-residential-01&orientation=landscape"
                    alt="Solar panel installation"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="relative rounded-xl overflow-hidden group">
                  <img
                    src="https://readdy.ai/api/search-image?query=Solar%20water%20pump%20working%20in%20green%20agricultural%20field%20in%20India%2C%20water%20sprinkling%20on%20crops%2C%20bright%20sunlight%2C%20professional%20photography%2C%20warm%20tones&width=500&height=400&seq=about-pump-01&orientation=landscape"
                    alt="Solar water pump"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="relative rounded-xl overflow-hidden group">
                  <img
                    src="https://readdy.ai/api/search-image?query=Large%20commercial%20solar%20panel%20array%20on%20industrial%20building%20roof%20in%20India%2C%20rows%20of%20panels%2C%20clear%20blue%20sky%2C%20professional%20photography%2C%20bright%20daylight&width=500&height=400&seq=about-commercial-01&orientation=landscape"
                    alt="Commercial solar"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="relative rounded-xl overflow-hidden group">
                  <img
                    src="https://readdy.ai/api/search-image?query=Agricultural%20drone%20flying%20low%20over%20green%20crop%20field%20in%20India%20spraying%20fine%20mist%2C%20golden%20hour%20lighting%2C%20professional%20photography%2C%20high%20detail%2C%20dynamic%20action%20shot&width=500&height=400&seq=about-drone-01&orientation=landscape"
                    alt="Agricultural drone spraying"
                    className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </SlideInRight>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-12 md:mb-16">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Leadership</span>
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 mt-3">Meet Our Founder</h2>
            </div>
          </FadeUp>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <SlideInLeft>
                <div className="relative">
                  {/* Background accent shape */}
                  <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl bg-primary-100/50"></div>
                  <div className="relative rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 aspect-[4/5] max-w-md mx-auto">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/1f8780b95eea472df48319064d2eefe4.png"
                      alt="Shaik Hyder Pasha - Founder & Managing Director of NexSunNexova"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </SlideInLeft>

              <SlideInRight delay={150}>
                <div className="max-w-xl">
                  <h3 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mb-1">Shaik Hyder Pasha</h3>
                  <p className="text-base text-primary-600 font-semibold mb-6">Founder & Managing Director</p>

                  <blockquote className="relative pl-5 border-l-4 border-accent-400 mb-6">
                    <p className="text-base md:text-lg text-foreground-600 leading-relaxed italic">
                      "Our mission is simple — make clean solar energy affordable and accessible to every home, farm, and business in Telangana."
                    </p>
                  </blockquote>

                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    With a vision to democratize solar energy access in Telangana, Shaik Hyder Pasha established NexSunNexova to bridge the gap between advanced solar technology and everyday consumers. His hands-on approach ensures every project meets the highest standards of quality and customer satisfaction.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    Under his leadership, the company has expanded beyond solar installations into precision agricultural drone spraying and EV charging infrastructure — creating a comprehensive clean energy and smart farming ecosystem for homes, farms, and businesses across the state.
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <a href="tel:9603919373" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all shadow-md shadow-primary-500/20 whitespace-nowrap">
                      <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                      9603919373
                    </a>
                    <a href="tel:7893549373" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-all whitespace-nowrap">
                      <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                      7893549373
                    </a>
                  </div>
                </div>
              </SlideInRight>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Board of Directors</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mt-3">Our Leadership Team</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">The dedicated professionals driving NexSunNexova's growth across solar, drone, and EV verticals.</p>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={120} className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Syed Salman */}
            <FadeUp delay={0}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-accent-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-accent-200/40 group-hover:bg-accent-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/0a125b9546dcdbd30d9b312373fcb47a.jpeg"
                      alt="Syed Salman - Director, Solar &amp; Drone Operations"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">Syed Salman</h3>
                <p className="text-sm text-accent-600 font-medium mb-3">Director, Solar &amp; Drone Operations</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Leading NexSunNexova's solar EPC and agricultural drone spraying divisions with hands-on expertise in clean energy and precision agriculture.
                </p>
                <a href="tel:9542760314" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-50 text-accent-600 text-sm font-medium hover:bg-accent-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  9542760314
                </a>
              </div>
            </FadeUp>

            {/* MD. Sharfoddin */}
            <FadeUp delay={100}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-primary-200/40 group-hover:bg-primary-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/7052c0aaaefcd4d63234395b6dcb00e3.jpeg"
                      alt="MD. Sharfoddin - Telangana Marketing Head, E.V Charging Stations"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">MD. Sharfoddin</h3>
                <p className="text-sm text-primary-600 font-medium mb-3">Telangana Marketing Head, E.V Charging Stations</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Key strategist behind NexSunNexova's operational excellence and business expansion across Telangana.
                </p>
                <a href="tel:6300774817" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  6300774817
                </a>
              </div>
            </FadeUp>

            {/* Shaik Hafeez */}
            <FadeUp delay={200}>
              <div className="group text-center p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/70 hover:border-secondary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="relative mx-auto mb-5 w-36 h-36 md:w-40 md:h-40">
                  <div className="absolute inset-0 rounded-full bg-secondary-200/40 group-hover:bg-secondary-300/40 transition-all"></div>
                  <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-background-50 shadow-md">
                    <img
                      src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/9dedf6844aa349e4498aa61984a00807.jpeg"
                      alt="Shaik Hafeez - Drone Operations, TS &amp; AP Marketing Head"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">Shaik Hafeez</h3>
                <p className="text-sm text-secondary-600 font-medium mb-3">Drone Operations, TS &amp; AP Marketing Head</p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                  Driving brand presence and customer outreach across Telangana and Andhra Pradesh markets.
                </p>
                <a href="tel:9581383871" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-50 text-secondary-600 text-sm font-medium hover:bg-secondary-100 transition-all whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  9581383871
                </a>
              </div>
            </FadeUp>
          </StaggerContainer>
        </div>
      </section>

      {/* Why Choose Us (detailed) */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Our Strengths</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Why Customers Trust Us</h2>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={100} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
            {whyChooseUs.map((item) => (
              <div key={item.title} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">Areas We Serve</h2>
              <p className="text-foreground-600 mt-2">Proudly serving communities across Telangana</p>
            </div>
          </FadeUp>
          <StaggerContainer staggerDelay={60} className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {serviceAreas.map((area) => (
              <span key={area} className="px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all hover:-translate-y-0.5 cursor-default shadow-sm">
                {area}
              </span>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <ScaleIn>
            <div className="max-w-3xl mx-auto text-center p-8 md:p-12 rounded-2xl bg-primary-500 text-background-50 shadow-xl shadow-primary-500/20">
              <h2 className="font-heading font-bold text-2xl md:text-3xl mb-3">Partner with Us</h2>
              <p className="text-base text-primary-100 mb-6">Join hundreds of satisfied customers who have made the switch to clean, affordable solar energy.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 font-semibold hover:bg-background-100 transition-all whitespace-nowrap hover:-translate-y-0.5">
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
                Get in Touch
              </Link>
            </div>
          </ScaleIn>
        </div>
      </section>
    </div>
  );
}